/**
 * Keeps the editor's in-memory dub description filled, so the dub engine has
 * something to build the dubbed audio from when a document is produced.
 *
 * The engine runs inside the timeline's synchronous serialiser, so it can
 * neither fetch nor wait. This hook is the half that can. It fetches, leaves
 * what it received in the holder inside the editor, and tells the editor, which
 * is what makes the next pass place the dub.
 *
 * It refreshes on three occasions, which are the three ways the description in
 * hand can stop being the right one:
 *
 * - **The story loaded, or changed.** A different story's dub is a different
 *   description, so everything held is dropped and the new one fetched.
 * - **The language changed.** Each language has its own description. Both are
 *   kept, which is what makes switching to a language and back produce the same
 *   audio both times with nothing regenerated.
 * - **An hour is nearly up.** The addresses inside a description are signed and
 *   stop being served. The refresh is scheduled to land while the ones in hand
 *   still work, so nothing ever goes silent waiting for it.
 *
 * Nothing here is saved. That is the point: a signed address written into a
 * stored clip is what used to leave a clip reopened the next day reading
 * "Dubbed" and playing silence.
 */
import { DUB_ADDRESS_LIFETIME_MS } from '@src/editor/dubbing/DubDescriptionStore';
import { EditorChangeType } from '@src/editor/types';
import { normalizeLangKey } from '@src/utility/bcp47';
import { LogManager } from '@src/utility/Logger';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useDubManifestFetch } from './useDubbedLanguages';

import type { VideoEditor } from '@src/editor/VideoEditor';

const logger = LogManager.getLogger('component:useDubDescriptionRefresh');

/**
 * How long before the hour is up to fetch again. Ten minutes of headroom, so a
 * slow request still lands while the addresses in hand are being served.
 */
const REFRESH_HEADROOM_MS = 10 * 60 * 1000;

/**
 * Where the fetch for the current language has got to.
 *
 * `missing` and `failed` are kept apart because only one of them is the
 * person's problem: the server saying this story has no dub in that language is
 * worth a sentence, and a network blip that leaves the description in hand
 * untouched is not.
 */
export type DubDescriptionStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'missing'
  | 'failed';

export interface DubDescriptionRefresh {
  /** Where the fetch for the language asked about has got to. */
  status: DubDescriptionStatus;
  /**
   * Fetch this language's description again, now. For the one case the
   * scheduled refresh cannot cover: the dub itself changed on the server under
   * a clip that is already playing it, and nothing here observed that.
   */
  refreshNow: () => void;
}

export interface UseDubDescriptionRefreshOptions {
  /** The editor whose holder is filled. `undefined` does nothing. */
  editor: VideoEditor | undefined;
  storyId: string | null;
  /**
   * The language the timeline is set to play, or `null` when it plays no dub.
   * `null` fetches nothing: there is no description to want.
   */
  language: string | null;
  /** "Is this the clip's original video?", for the engine to read. */
  isOriginalVideoElement: (probe: unknown) => boolean;
  /** The kept music bed's signed address, or `null`. */
  musicBedUrl: string | null;
  /** The separated voice's signed address, for the lead-in, or `null`. */
  voiceStemUrl?: string | null;
  /** Repaint the canvas once a description has arrived. */
  refreshSource: () => void;
  /** False on a workspace with dubbing switched off. Fetches nothing. */
  enabled: boolean;
}

export function useDubDescriptionRefresh({
  editor,
  storyId,
  language,
  isOriginalVideoElement,
  musicBedUrl,
  voiceStemUrl = null,
  refreshSource,
  enabled,
}: UseDubDescriptionRefreshOptions): DubDescriptionRefresh {
  const getDubManifest = useDubManifestFetch();
  const [status, setStatus] = useState<DubDescriptionStatus>('idle');
  // Bumped to ask the effect below to fetch again with everything else the same.
  const [refreshCount, setRefreshCount] = useState(0);
  const refreshNow = useCallback(() => setRefreshCount(count => count + 1), []);

  // Held in refs and read at the moment of use, so a fetch does not restart
  // every time the surface above re-renders with a new function object.
  const probeRef = useRef(isOriginalVideoElement);
  probeRef.current = isOriginalVideoElement;
  const refreshSourceRef = useRef(refreshSource);
  refreshSourceRef.current = refreshSource;
  // Which press of "fetch it again" the effect below last acted on.
  const lastRefreshRef = useRef(0);

  // ORDER MATTERS in the three effects below, and React runs them in the order
  // they are written. Clearing comes FIRST, because it wipes the music bed
  // address as well as the descriptions; setting the bed after it is what stops
  // a bed that was already in hand when the screen opened being thrown away and
  // never laid under the dub. Both of the two that follow list the story among
  // the values they watch, so a story change re-runs them after the clear too.

  // A different story's descriptions are the wrong ones, so they go rather than
  // sitting there to be read by a language of the same name.
  useEffect(() => {
    editor?.dubSource?.clear();
  }, [editor, storyId]);

  // Which element is the clip's own picture. Set outside the fetch below,
  // because the engine may run before any description has arrived and it still
  // has to know which element to take the level off.
  useEffect(() => {
    // Optional-chained through `dubSource` as well as `editor`, because a test
    // and a not-yet-built surface can both hand over an editor-shaped object
    // that has no holder on it, and this must not be what throws.
    editor?.dubSource?.setOriginalVideoProbe(probe => probeRef.current(probe));
  }, [editor, storyId]);

  useEffect(() => {
    editor?.dubSource?.setMusicBedUrl(musicBedUrl);
  }, [editor, musicBedUrl, storyId]);

  // The lead-in can be on before the story is separated, so the voice's
  // address can land after the dub was laid. Nothing else changes when it
  // does, so its arrival asks for the next pass, as a description's does.
  useEffect(() => {
    editor?.dubSource?.setVoiceStemUrl?.(voiceStemUrl);
    if (!editor || !voiceStemUrl) return;
    editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
    refreshSourceRef.current();
  }, [editor, voiceStemUrl, storyId]);

  // PLAT-11905 — a caption produce in this language asks for a fresh
  // description, so captions generated after a re-dub follow the new timing.
  // Answered with the same fetch as every other refresh.
  useEffect(() => {
    if (!enabled || !language) return undefined;
    const wanted = normalizeLangKey(language);
    return editor?.dubSource?.onRefreshRequested?.(requested => {
      if (normalizeLangKey(requested) === wanted) refreshNow();
    });
  }, [editor, enabled, language, refreshNow]);

  useEffect(() => {
    if (!enabled || !editor?.dubSource || !storyId || !language) {
      setStatus('idle');
      return undefined;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const fetchAndHold = async (): Promise<void> => {
      setStatus('loading');
      try {
        const resolved = await getDubManifest({ storyId, language });
        if (cancelled) return;

        if (!resolved) {
          // The server's authoritative "this story has no dub in that
          // language". Dropping it is what takes the audio out of the next
          // document, rather than leaving a dead description to be placed.
          editor.dubSource.forget(language);
          setStatus('missing');
        } else {
          editor.dubSource.remember(language, resolved);
          setStatus('ready');
        }

        // The arrival is what triggers the next pass of the engine: the
        // timeline is unchanged, so nothing else would ask for a new document.
        editor.notifyObservers(EditorChangeType.TIMELINE_CHANGED);
        refreshSourceRef.current();
      } catch (error) {
        // Advisory. A description that could not be fetched leaves whatever is
        // held in place, so a network blip never takes a working dub off the
        // screen. The scheduled retry below still runs.
        if (cancelled) return;
        setStatus('failed');
        logger.warn('Could not fetch the dub description for this language', {
          storyId,
          language,
          error,
        });
      }
    };

    // Scheduled rather than repeated on an interval, so a description that
    // arrived a moment ago is not fetched again on the next render.
    const scheduleNext = () => {
      // The effect may have been torn down while the fetch above was out, in
      // which case the cleanup has already cleared the timer it knew about and
      // a fresh one would outlive the screen.
      if (cancelled) return;
      timer = setTimeout(
        () => {
          if (cancelled) return;
          void fetchAndHold().then(scheduleNext);
        },
        Math.max(DUB_ADDRESS_LIFETIME_MS - REFRESH_HEADROOM_MS, 60_000),
      );
    };

    // A fetch is asked for when the description in hand is missing or old, and
    // once more for each press of "fetch it again". Compared against the count
    // this hook last acted on rather than against nothing, so one manual
    // refresh does not make every later language change refetch a description
    // that is perfectly fresh.
    const askedForAgain = refreshCount > lastRefreshRef.current;
    lastRefreshRef.current = refreshCount;

    if (askedForAgain || editor.dubSource.needsRefresh(language)) {
      void fetchAndHold().then(scheduleNext);
    } else {
      setStatus('ready');
      scheduleNext();
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [editor, enabled, getDubManifest, language, refreshCount, storyId]);

  return { status, refreshNow };
}
